import db from "../db/database";
import {
	fetchFromBusinessCentral,
	sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { updateClientNoInInvoices } from "./invoiceService";
import { getPendingSync, isOnline } from "./syncService";

// Función para transformar un cliente al formato esperado por Business Central
export const transformClientForBC = (client) => {

	//Incluimos todas las propiedades del cliente ya que en algunos paises son más
	const transformedClient = { ...client };

	// Asignamos o sobreescribimos los campos obligatorios o con default
	transformedClient.No_ = client.No || "TEMP"; // BC asigna si no existe
	transformedClient.Name = client.name || "";
	transformedClient.Direccion = client.Direccion || "";
	transformedClient.Direccion_2 = client.Direccion_2 || "";
	transformedClient.Poblacion = client.Poblacion || "";
	transformedClient.Cod_Postal = client.Cod_Postal || "";
	transformedClient.Pais = client.Pais || "";
	transformedClient.Telefono = client.Telefono || "";
	transformedClient.Mobil = client.Mobil || "";
	transformedClient.E_Mail = client.E_Mail || "";
	transformedClient.Customer_Price_Group = client.Customer_Price_Group || "";
	transformedClient.Contacto = client.Contacto || "";
	transformedClient.Tipo_Documento_SrI = client.Tipo_Documento_SrI || "Cedula";
	transformedClient.Numero_Identificacion_fiscal = client.Numero_Identificacion_fiscal || "";
	transformedClient.POS_Discount = client.POS_Discount ?? 0;
	transformedClient.Territory_Code = client.Territory_Code || 217;
	transformedClient.Deleted = client.deleted || false;

	// Creamos el formato final esperado por el end point
	return {
		data: JSON.stringify({ Customers: [transformedClient] }),
	};
};

export const syncClientById = async (clientId) => {
	if (!isOnline()) {
		return { ok: false, error: "No hay conexión a internet" };
	}

	try {
		const client = await db.clients.get(clientId);
		if (!client) {
			return { ok: false, error: "Cliente no encontrado" };
		}

		if (client.deleted) {
			await db.clients.delete(client.id);
			return { ok: true, client: null };
		}

		const response = await sendToBusinessCentral(
			"Importaciones_insertaClientes",
			transformClientForBC(client),
			true
		);

		if (!response?.value) {
			return {
				ok: false,
				error:
					response?.error ||
					`Respuesta inválida de BC al sincronizar el cliente ${client.id}`,
			};
		}

		const clientNo = response.value;

		//Recupero el cliente actualizado desde BC
		const updatedClient = await fetchFromBusinessCentral("ListaClientes" + `?$filter=No eq '${clientNo}'`);

		if (updatedClient.length > 0) {
			const clientUpdated = updatedClient[0];
			const clientToUpdate = {
				...clientUpdated,
				lastModified: new Date(),
				lastSynced: new Date(),
				pendingSync: 0,
			}

			await db.clients.update(client.id, clientToUpdate);
		}
		else {
			return {
				ok: false,
				error: `No se pudo obtener el cliente ${clientNo} actualizado desde Business Central`,
			};
		}

		await updateClientNoInInvoices(client.id, clientNo);

		return { ok: true, client: await db.clients.get(client.id) };
	} catch (error) {
		console.error(`Error al sincronizar cliente ${clientId}:`, error);
		return {
			ok: false,
			error:
				error?.message ||
				`No se pudo sincronizar el cliente ${clientId} con Business Central`,
		};
	}
};

export const syncClients = async () => {
	if (!isOnline()) {
		console.log("No hay conexión a internet");
		return false;
	}

	try {
		const pendingClients = await getPendingSync("clients");

		// Enviar clientes pendientes uno por uno
		if (pendingClients.length > 0) {

			for (const client of pendingClients) {
				try {
					// Si el cliente está marcado como eliminado, lo eliminamos directamente
					if (client.deleted) {
						await db.clients.delete(client.id);
						continue;
					}
					const result = await syncClientById(client.id);
					if (!result.ok) {
						console.error(
							`Error al sincronizar cliente ${client.id}: ${result.error}`
						);
					}
				} catch (error) {
					console.error(`Error al sincronizar cliente ${client.id}:`, error);
				}
			}
		}

		const localClients = await db.clients.toArray();

		let lastSourceCode = null;

		if (localClients.length > 0) {
			lastSourceCode = localClients.sort((a, b) => a.Source_Counter - b.Source_Counter)[localClients.length - 1].Source_Counter;
		}


		// Obtener clientes del servidor solo después de sincronizar los pendientes
		const serverClients = await fetchFromBusinessCentral("ListaClientes" + (lastSourceCode ? `?$filter=Source_Counter gt ${lastSourceCode}` : ""));

		// Mapa para buscar clientes locales por No rápidamente
		const localClientsByNo = {};
		localClients.forEach((client) => {
			if (client.No) {
				localClientsByNo[client.No] = client;
			}
		});

		// Actualizar clientes existentes y añadir nuevos desde el servidor
		for (const serverClient of serverClients) {
			// Verificar si el cliente ya existe localmente
			const localClient = localClientsByNo[serverClient.No];

			if (localClient) {
				// Actualizar cliente existente si no está pendiente de sincronización
				if (localClient.pendingSync === 0) {
					await db.clients.update(localClient.id, {
						...serverClient,
						name: serverClient.Name,
						Direccion: serverClient.Direccion || "",
						Direccion_2: serverClient.Direccion_2 || "",
						Poblacion: serverClient.Poblacion || "",
						Cod_Postal: serverClient.Cod_Postal || "",
						Pais: serverClient.Pais || "",
						Telefono: serverClient.Telefono || "",
						Mobil: serverClient.Mobil || "",
						E_Mail: serverClient.E_Mail || "",
						Customer_Price_Group: serverClient.Customer_Price_Group || "",
						Contacto: serverClient.Contacto || "",
						Tipo_Documento_SrI: serverClient.Tipo_Documento_SrI || "Cedula",
						Numero_Identificacion_fiscal:
							serverClient.Numero_Identificacion_fiscal || "",
						POS_Discount: serverClient.POS_Discount || 0,
						Source_Counter: serverClient.Source_Counter || 0,
						lastModified: new Date(),
						lastSynced: new Date(),
					});
				}
			} else {
				// Añadir nuevo cliente del servidor si no existe localmente
				await db.clients.add({
					...serverClient,
					No: serverClient.No,
					name: serverClient.Name,
					Direccion: serverClient.Direccion || "",
					Direccion_2: serverClient.Direccion_2 || "",
					Poblacion: serverClient.Poblacion || "",
					Cod_Postal: serverClient.Cod_Postal || "",
					Pais: serverClient.Pais || "",
					Telefono: serverClient.Telefono || "",
					Mobil: serverClient.Mobil || "",
					E_Mail: serverClient.E_Mail || "",
					Customer_Price_Group: serverClient.Customer_Price_Group || "",
					Contacto: serverClient.Contacto || "",
					Tipo_Documento_SrI: serverClient.Tipo_Documento_SrI || "Cedula",
					Numero_Identificacion_fiscal:
						serverClient.Numero_Identificacion_fiscal || "",
					POS_Discount: serverClient.POS_Discount || 0,
					Source_Counter: serverClient.Source_Counter || 0,
					lastSynced: new Date(),
					pendingSync: 0,
					deleted: false,
				});
			}
		}

		return true;
	} catch (error) {
		console.error("Error al sincronizar clientes:", error);
		return false;
	}
};
